import React, { useEffect, useMemo, useRef, useState } from 'react'
import Text from '../../../../../elements/Typo/Text'
import AddFilters from './addFilters'
import Sql from './sql'
import { FALLBACK_TABS, FilterTab, proceedOrPromptToSaveQuery, resolveInitialTab, resolveStartTab, resolveVisibleTabs } from './filterTabs'
import LazyNlToSqlFilter from './LazyNlToSqlFilter'
import Nl2sqlTabLayout from './Nl2sqlTabLayout'
import { useQuerySaveBlocker, useQuerySaveState } from './useChatQuerySave'
import { useChatFilterRestore } from './useChatFilterRestore'
import FilterTabButton from './FilterTabButton'
import DraftPreviewNotice from './DraftPreviewNotice'
import { useDraftPreview } from './useDraftPreview'
import { filterTabConfig } from '../../../../../_config/filterTabConfig'
import CustomAgGrid from '../../../../../components/CustomAgGrid'
import Button from '../../../../../elements/button'
import { useNavigate, useParams } from 'react-router-dom'
import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'
import toast from 'react-hot-toast'
import { useExperimentSelector } from '../../../../../store/selectors/experimentSelector'
import { AgGridReact } from 'ag-grid-react'
import { URLS } from '../../../../../_config'
import { useDialog } from '../../../../../store/api/hooks/dialogHook'
import { useFpLoader } from '../../../../../store/api/hooks/fpLoaderHook'
import { changeToAddFilterTab, processDataColumnResponse, trackEvent } from '../../../../../_common/functions'
import { useComponentFunctions } from '../../../../../_common/hook/useComponentFunctions'
import Loader from '../../../../../components/loader'
import { useTranslation } from 'react-i18next'
import i18n from '../../../../../i18n'
import { startTiming, stopTiming } from '../../../../../utils/analytics/rumTracking'

interface ProcessDataProps {
    experimentLiteral?: string
    onComplete?: (message: string) => void
    hidePreviousStep?: boolean
    // Tabs a host wants shown; overrides the product tab flags for the existing tabs.
    visibleTabs?: FilterTab[]
}

const ProcessData: React.FC<ProcessDataProps> = ({ experimentLiteral, onComplete, hidePreviousStep, visibleTabs: requestedTabs }) => {
    const { t } = useTranslation()
    const processDataGridRef = useRef<AgGridReact>(null)
    const processAdditionalDataGridRef = useRef<AgGridReact>(null)
    const navigate = useNavigate()
    const params = useParams()
    const reduxExperiment = useExperimentSelector()
    const experimentApi = useExperimentApi()
    const [fetchedExperiment, setFetchedExperiment] = useState<any>(null)

    const literal = experimentLiteral || params.experiment
    const experiment = experimentLiteral ? fetchedExperiment : reduxExperiment

    useEffect(() => {
        if (experimentLiteral) {
            experimentApi.getExperimentDetailsWatch(
                { literal: experimentLiteral + '?use_orm=true' },
                (resp: any) => {
                    setFetchedExperiment(resp)
                },
                (error: any) => {
                    toast.error(error?.message || 'Failed to fetch experiment details')
                },
            )
        }
    }, [experimentLiteral])
    // The chat host (experimentLiteral) always shows the two existing tabs, whatever the product flags say.
    const visibleTabs = useMemo(() => resolveVisibleTabs(filterTabConfig, requestedTabs ?? (experimentLiteral ? FALLBACK_TABS : undefined), !!experiment?.user_design), [requestedTabs, experimentLiteral, experiment?.user_design])
    const nl2sqlEnabled = visibleTabs.includes('nl2sql')
    // Unchanged when the NL2SQL tab is off: the page opens on "Add Filters" exactly as before.
    const [filterTab, setFilterTab] = useState<FilterTab>(() => resolveStartTab(experiment, visibleTabs))
    const initialTabResolvedRef = useRef(!!experiment)
    const savedStateRestoredRef = useRef(false)
    const [rowData, setRowData] = useState<any>(null)
    const [columnDefs, setcolumnDefs] = useState<[]>([])
    const [additionalRowData, setAdditionalRowData] = useState<any>(null)
    const [additionalcolumnDefs, setAdditionalcolumnDefs] = useState<[]>([])
    const { showLoader, hideLoader } = useFpLoader()
    const dialog = useDialog()
    const [columnPreviewData, setcolumnPreviewData] = useState<[]>([])
    const [filters, setFilters] = useState<any>(null)
    const [sqls, setSqls] = useState<any>(null)
    const [working, setWorking] = useState<boolean>(false)
    const [localLoading, setLocalLoading] = useState<boolean>(false)
    const [completed, setCompleted] = useState<boolean>(false)
    const [currentQueryExecuted, setCurrentQueryExecuted] = useState<boolean>(false)
    const [dataPreviewTable, setDataPreviewTable] = useState<any>(null)
    // True from the moment the chat tab applies a query until its preview refresh settles, so Next can't advance on stale data.
    const [chatApplying, setChatApplying] = useState<boolean>(false)
    const sqlRef: any = useRef()
    const componentFunctions = useComponentFunctions()
    // Skip the next blocker prompt when the user has already answered it via the Next button flow.
    const skipNextBlockerPromptRef = useRef(false)
    // Id of the latest preview request; a response for an older one is dropped so it can't overwrite newer rows, table and sqls.
    const previewRequestRef = useRef(0)
    const { draft, previewDraft, clearDraft, displayRows, displayColumns } = useDraftPreview((payload, onSuccess, onError) => experimentApi.getFilterPreviwData({ literal, page: 1, per_page: 40, ...payload }, onSuccess, onError))
    const {
        status: chatRestoreStatus,
        savedFilter: chatSavedFilter,
        restore: restoreChatFilter,
    } = useChatFilterRestore({
        literal,
        filterKey: 'design_table_filters',
        previewRequestRef,
        getExperimentDetails: experimentApi.getFilterRestoreDetails,
        preview: (payload, onSettled) => getFilterPreviwData(payload, false, onSettled),
        setFilters,
        // A canceled (reset or unmounted) preview never settles, so its loader must be released here.
        onCancel: () => stopLoading(),
    })

    const { currentSavedSql, chatSave, querySavePending, saveQuery, blocker } = useQuerySaveState({
        filterTab,
        filterKey: 'design_table_filters',
        experiment,
        chatSavedFilter,
        sqls,
        currentQueryExecuted,
        sqlRef,
        experimentLiteral,
    })

    const Next = (data: any) => {
        trackEvent('wizard_opened', {
            timestamp: new Date().toISOString(),
            experiment_id: literal,
            step: Math.max(experiment?.step ?? 0, 3),
            experiment_type: data?.data[0]?.experiment_type,
        })
        if (onComplete) {
            setCompleted(true)
            onComplete('I have applied the filters')
            return
        }
        let url: string = URLS.CREATE_EXPERIMENT(params.experiment)
        if (experiment.step > 3) {
            url += '?step=3'
        }
        void navigate(url)
    }

    const GenerateDataQualityReport = (data: any) => {
        if (sqls?.sql_query) {
            setWorking(true)
            experimentApi.generateDataQualityReport(
                {
                    literal,
                    query: sqls.sql_query,
                    upload_type: 'design',
                },
                (data: any) => {
                    setWorking(false)
                    Next(data)
                },
                (error: any) => {
                    setWorking(false)
                    toast.error(error?.message)
                },
            )
        } else {
            Next(data)
        }
    }

    const startLoading = () => {
        if (experimentLiteral) {
            setLocalLoading(true)
        } else {
            showLoader()
        }
    }

    const stopLoading = () => {
        if (experimentLiteral) {
            setLocalLoading(false)
        } else {
            hideLoader()
        }
    }

    // markExecuted=false: chat-generated SQL is not a user-authored query, so it must not trigger the "save as feature" prompt.
    const getFilterPreviwData = (payload: any = {}, markExecuted = true, onSettled?: (success: boolean) => void) => {
        clearDraft()
        const requestId = ++previewRequestRef.current
        startLoading()
        startTiming('data_preview_load')
        experimentApi.getFilterPreviwData(
            {
                literal,
                page: 1,
                per_page: 40,
                ...payload,
            },
            (data: any) => {
                if (requestId !== previewRequestRef.current) {
                    onSettled?.(false)
                    return
                }
                processDataColumnResponse(experiment, data.data.result, setcolumnDefs, setRowData, setcolumnPreviewData, stopLoading)
                stopTiming('data_preview_load', { table_type: payload?.table_type ?? data?.data?.table_name ?? '', row_count: data?.data?.result?.length })
                if (experiment?.data_mappings?.additional_design_table_name) {
                    componentFunctions.onAdditionalCsvUploaded('AdditionalDesign', literal || '', setAdditionalcolumnDefs, setAdditionalRowData)
                }
                if (payload?.sql_query) {
                    trackEvent('wizard_sql_success', { row_count: data?.data?.result?.length })
                    if (markExecuted) setCurrentQueryExecuted(true)
                }
                setDataPreviewTable(data?.data?.table_name)
                setSqls(payload)
                onSettled?.(true)
            },
            (error: any) => {
                if (requestId !== previewRequestRef.current) {
                    onSettled?.(false)
                    return
                }
                stopLoading()
                setRowData([])
                if (payload?.sql_query) {
                    trackEvent('wizard_sql_error', { error_message: t(error?.message) })
                }
                if (error?.message) {
                    toast.error(t(error?.message))
                } else {
                    toast.error(t(error))
                }
                onSettled?.(false)
            },
        )
    }

    const applyFilter = (filters: any) => {
        trackEvent('wizard_filter_applied', { filter_count: filters?.filters ? Object.keys(filters.filters).length : 0 })
        setFilters(filters)
        getFilterPreviwData(filters)
    }

    const applySQL = (SQL: any) => {
        getFilterPreviwData(SQL)
    }

    const updateExperiment = () => {
        const paramspost: any = {
            step: Math.max(experiment?.step ?? 0, 3),
            is_draft: true,
            literal,
            sql_design_table_name: dataPreviewTable,
        }
        if (filters) {
            const filterval = filters?.filters
            paramspost.design_table_filters = {
                filters: filterval,
            }
        }
        if (sqls?.sql_query) {
            paramspost.design_table_filters = {
                sql_query: sqls.sql_query,
            }
        }

        experimentApi.updateExperiment(
            paramspost,
            (data: any) => {
                trackEvent('wizard_step2_completed', { experiment_id: literal })
                GenerateDataQualityReport(data)
            },
            (error: any) => {
                trackEvent('wizard_step_api_error', { step: 2, error_code: error?.message })
                toast.error(error?.message)
            },
        )
    }

    const enterNl2sql = () => {
        savedStateRestoredRef.current = true
        setFilterTab('nl2sql')
        setCurrentQueryExecuted(false)
        restoreChatFilter()
    }

    const goToNl2sql = () => {
        if (filterTab === 'nl2sql' && chatRestoreStatus !== 'failed') return
        trackEvent('wizard_scope_tab_switched', { tab: 'nl2sql' })
        proceedOrPromptToSaveQuery({ currentQueryExecuted: querySavePending, dialog, t, sqlRef: { current: { saveFeatures: saveQuery } }, proceed: enterNl2sql })
    }

    // Saved work for an experiment whose saved tab is hidden: the chat tab restores it, as the hidden tab would have.
    useEffect(() => {
        if (filterTab !== 'nl2sql' || !experiment || savedStateRestoredRef.current) return
        const saved = experiment.experiment_metadatas?.design_table_filters
        if (saved?.sql_query) {
            savedStateRestoredRef.current = true
            getFilterPreviwData({ sql_query: saved.sql_query }, false)
        } else if (saved?.filters && Object.keys(saved.filters).length > 0) {
            savedStateRestoredRef.current = true
            applyFilter({ filters: saved.filters })
        }
    }, [experiment, filterTab])

    const enterSql = () => {
        setFilterTab('sql')
        getFilterPreviwData()
        setFilters(null)
        setSqls(null)
    }

    const goToSql = () => {
        trackEvent('wizard_scope_tab_switched', { tab: 'sql' })
        proceedOrPromptToSaveQuery({ currentQueryExecuted: chatSave.unsaved, dialog, t, sqlRef: { current: { saveFeatures: saveQuery } }, proceed: enterSql })
    }

    // With the NL2SQL tab on, saved-state tab selection is decided once (initial render / first load) so a later
    // experiment refresh, e.g. after attaching a CSV, never moves the user off the chat.
    const restoreSqlTabOrFallback = () => {
        if (!experiment?.experiment_metadatas?.design_table_filters?.sql_query || nl2sqlEnabled) return
        if (visibleTabs.includes('sql')) {
            setFilterTab('sql')
        } else if (!savedStateRestoredRef.current) {
            savedStateRestoredRef.current = true
            getFilterPreviwData({ sql_query: experiment.experiment_metadatas.design_table_filters.sql_query }, false)
        }
    }

    const resolveInitialNl2sqlTab = () => {
        if (!nl2sqlEnabled || !experiment || initialTabResolvedRef.current) return
        initialTabResolvedRef.current = true
        setFilterTab(resolveInitialTab(experiment, visibleTabs))
    }

    const loadInitialPreview = () => {
        if (!experiment || rowData != null) return
        const saved = experiment.experiment_metadatas?.design_table_filters
        if (saved?.sql_query) return
        if (saved?.filters && Object.keys(saved.filters).length > 0) {
            // The Add Filters tab restores its own saved filters; with no tab to do it, apply them here.
            if (!nl2sqlEnabled && !visibleTabs.includes('filter')) applyFilter({ filters: saved.filters })
            return
        }
        getFilterPreviwData()
        setFilters(null)
        setSqls(null)
    }

    useEffect(() => {
        restoreSqlTabOrFallback()
        resolveInitialNl2sqlTab()
        loadInitialPreview()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experiment])

    const isQuerySavePending = (next: boolean = false) => {
        if (querySavePending) {
            dialog.confirm({
                type: 'delete',
                title: t('Save this query for future use?'),
                message: t('Would you like to save this query as a reusable Feature for future experiments?'),
                //warning: 'By Changing this, it will alter the existing experiment details',
                cancelButtonLabel: t('No, Not Now'),
                confirmButtonLabel: t('Yes, Save Query'),
                onConfirm: () => {
                    saveQuery()
                    blocker.reset?.()
                },
                onCancel: () => {
                    if (next) {
                        skipNextBlockerPromptRef.current = true
                        updateExperiment()
                    } else {
                        changeToAddFilterTab(experiment, setFilterTab, getFilterPreviwData, setFilters, setSqls)
                        blocker.proceed?.()
                    }
                },
            })
        } else {
            changeToAddFilterTab(experiment, setFilterTab, getFilterPreviwData, setFilters, setSqls)
        }
    }

    useQuerySaveBlocker(blocker, isQuerySavePending, skipNextBlockerPromptRef)

    if (experimentLiteral && !fetchedExperiment) {
        return (
            <div className="flex justify-center items-center py-10">
                <Loader />
            </div>
        )
    }

    if (completed) {
        return (
            <div className="flex items-center gap-2 py-3 text-green-700">
                <span className="text-sm font-medium">&#10003; {t('Filters applied successfully')}</span>
            </div>
        )
    }

    return (
        <>
            {localLoading && (
                <div className="flex justify-center items-center py-10">
                    <Loader />
                </div>
            )}
            <div className="flex items-center gap-3 h-[2.667rem] border-b border-[#00000014] my-2" data-aos="fade-up" data-aos-delay="300">
                {visibleTabs.includes('nl2sql') && <FilterTabButton label={t('Chat to Filter')} active={filterTab == 'nl2sql'} onClick={goToNl2sql} testId="nl2sql-tab" />}
                {visibleTabs.includes('filter') && (
                    <FilterTabButton
                        label={t('Add Filters to your selected Data')}
                        active={filterTab == 'filter'}
                        className={`relative h-full flex items-center ${experiment?.user_design ? 'opacity-30' : 'cursor-pointer'}`}
                        onClick={() => {
                            trackEvent('wizard_scope_tab_switched', { tab: 'filter' })
                            isQuerySavePending()
                        }}
                        testId="filter-tab"
                    />
                )}
                {visibleTabs.includes('sql') && <FilterTabButton label={t('Create your own features')} active={filterTab == 'sql'} onClick={goToSql} testId="sql-tab" />}
            </div>

            {filterTab == 'nl2sql' && (
                <Nl2sqlTabLayout save={chatSave} query={sqls?.sql_query}>
                    {chatRestoreStatus === 'loading' && <Loader />}
                    {chatRestoreStatus === 'failed' && (
                        <Button theme="secondary" size="xs" onClick={enterNl2sql}>
                            {t('Try Again')}
                        </Button>
                    )}
                    {chatRestoreStatus === 'ready' && (
                        <LazyNlToSqlFilter
                            literal={literal}
                            savedFilterSql={currentSavedSql ?? null}
                            attachedTableName={experiment?.data_mappings?.additional_design_table_name}
                            onApplySql={(payload) => {
                                setChatApplying(true)
                                getFilterPreviwData(payload, false, () => setChatApplying(false))
                            }}
                            onAdditionalCsvUploaded={() => componentFunctions.onAdditionalCsvUploaded('AdditionalDesign', literal || '', setAdditionalcolumnDefs, setAdditionalRowData)}
                            onPendingChange={setChatApplying}
                            onPreviewSql={previewDraft}
                        />
                    )}
                </Nl2sqlTabLayout>
            )}
            {filterTab == 'filter' && <AddFilters tables={columnPreviewData} applyFilter={applyFilter} experimentLiteral={experimentLiteral} />}
            {filterTab == 'sql' && (
                <Sql
                    _ref={sqlRef}
                    applySQL={applySQL}
                    onadditionalCsvUploaded={() => componentFunctions.onAdditionalCsvUploaded('AdditionalDesign', literal || '', setAdditionalcolumnDefs, setAdditionalRowData)}
                    currentQueryExecuted={currentQueryExecuted}
                    setCurrentQueryExecuted={setCurrentQueryExecuted}
                />
            )}

            <div>
                <div className="flex items-center gap-3">
                    <Text size="xl" className="my-4 font-semibold" data-testid="data-preview-title">
                        {t('Data Preview')}
                    </Text>
                    <Text
                        size="sm"
                        className="px-2 py-1 font-semibold bg-[#E6E5E5] rounded flex-shrink whitespace-nowrap truncate"
                        title={dataPreviewTable || experiment?.data_mappings?.user_created_table_name || experiment?.data_mappings?.delta_design_table_name || '-'}
                    >
                        {dataPreviewTable || experiment?.data_mappings?.user_created_table_name || experiment?.data_mappings?.delta_design_table_name || experiment?.data_mappings?.additional_design_table_name || '-'}
                    </Text>
                </div>

                <DraftPreviewNotice draft={draft} />
                <div className="ag-theme-alpine">
                    <CustomAgGrid
                        key={'pdata1_' + i18n.language}
                        gridRef={processDataGridRef}
                        rowData={displayRows(rowData)}
                        columnDefs={displayColumns(columnDefs)}
                        defaultColDef={{ sortable: false, filter: false }}
                        paginationPageSize={20}
                        paginationPageSizeSelector={[10, 20, 50]}
                        pagination={false}
                        customPagination={true}
                        style={{ height: 350 }}
                        theme="light"
                        data-testid="data-preview-grid"
                    />
                </div>
            </div>
            {additionalRowData && additionalRowData.length > 0 && (
                <div>
                    <div className="flex items-center gap-3">
                        <Text size="xl" className="my-4 font-semibold" data-testid="data-preview-title">
                            {t('Additional Data Preview')}
                        </Text>
                        <Text size="sm" className="px-2 py-1 font-semibold bg-[#E6E5E5] rounded flex-shrink whitespace-nowrap truncate" title={experiment?.data_mappings?.additional_design_table_name}>
                            {experiment?.data_mappings?.additional_design_table_name ?? '-'}
                        </Text>
                    </div>

                    <div className="ag-theme-alpine">
                        <CustomAgGrid
                            key={'pdata2_' + i18n.language}
                            gridRef={processAdditionalDataGridRef}
                            rowData={additionalRowData}
                            columnDefs={additionalcolumnDefs}
                            defaultColDef={{ sortable: false, filter: false }}
                            paginationPageSize={20}
                            paginationPageSizeSelector={[10, 20, 50]}
                            pagination={false}
                            customPagination={true}
                            style={{ height: 350 }}
                            theme="light"
                            data-testid="data-preview-grid"
                        />
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between mt-4 mb-2" data-testid="footer-buttons">
                <div className="flex items-center gap-3">
                    {!hidePreviousStep && (
                        <Button
                            theme="secondary"
                            styleType="outline"
                            size="xs"
                            onClick={() => {
                                trackEvent('wizard_back_clicked', { experiment: literal })
                                void navigate(URLS.CREATE_EXPERIMENT(params.experiment) + '?step=1')
                            }}
                            data-testid="previous-step-button"
                        >
                            {t('Previous Step')}
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        theme="secondary"
                        size="xs"
                        className="text-white"
                        disabled={rowData?.length == 0 || working || chatApplying || (filterTab === 'nl2sql' && chatRestoreStatus !== 'ready')}
                        onClick={() => {
                            trackEvent('wizard_step2_next_clicked', {})
                            if (querySavePending) {
                                isQuerySavePending(true)
                            } else {
                                updateExperiment()
                            }
                        }}
                        data-testid="next-step-button"
                    >
                        {working ? <Loader /> : t('NEXT STEP')}
                    </Button>
                </div>
            </div>

            {working && <div className="text-end text-yellow-600 font-medium text-[0.65rem] italic">{t('It may take a few minutes to process your data. It depends on your table size, Please do not close or refresh the page.')}</div>}
        </>
    )
}

export default ProcessData
